import {HardwareItem} from "../../../../../../api/Hardware";
import {inches} from "../../const";
import {ElevationApp} from "../../ElevationApp";
import {hingeSideOrDefault} from "../../scenes/frameProfile/Helpers";
import {HardwareCategoryFields, hasCategory} from "../../shared-2d/hardwareGlyphs/HardwareGlyph";
import {CENTER_LOCK_ABOVE_BOTTOM, DEADBOLT_ABOVE_BOTTOM, MdHardwarePlacement, STANDARD_BACKSET} from "./MdHardware";

// Placement rules for door hardware, keyed by product category. A rule on a category
// applies to every item under it in the product category tree; a rule on a deeper
// category overrides, field by field, the rules above it (a field set to null drops the
// ancestor's limit). All distances are scene units (mm); the hub is the placement point
// the dimensions measure to.
export type HardwarePlacementRule = {
    // the product category this rule is attached to, at any level of the tree.
    category: string;
    // "clamp" (the default) keeps drags and dimension edits inside the limits; "flag" lets
    // the placement go anywhere and only marks it red. A placement outside the limits for
    // another reason (a stored design, a resized door) is flagged either way.
    enforce?: "clamp" | "flag";
    // the face of the door the hardware mounts on, as the Locations view names them:
    // "front" is seen from the opening's "from" side, "back" from its "to" side. Unset
    // (or null) shows the item on both faces.
    face?: "front" | "back" | null;
    // the category whose door placement on the same leaf this item's placement lines up
    // with (an exit trim on its exit device). The two placements share one position
    // until the user unlinks them (see MdHardware.link), and this item takes that
    // category's standardLocation unless it sets its own.
    linkTo?: string | null;
    // hub distance from the strike edge (the edge opposite the hinges).
    minFromStrikeEdge?: number | null;
    maxFromStrikeEdge?: number | null;
    // hub height above the finished floor (the leaf bottom plus the undercut).
    minAboveFloor?: number | null;
    maxAboveFloor?: number | null;
    // where Reset puts the hub, from the leaf it sits on. Without one, a primary handle /
    // lock goes back to its seeded lock height and backset, and anything else to the centre
    // of the leaf (see standardPosition).
    standardLocation?: ((leaf: RuleLeaf) => StandardLocation) | null;
};

// a standard hub location as the rules express it: from the strike edge and above the
// finished floor.
export type StandardLocation = {fromStrikeEdge: number; aboveFloor: number};

// the leaf a rule measures against: size, the undercut below the leaf bottom, and which
// edge the hinges are on.
export type RuleLeaf = {width: number; height: number; undercut: number; hingeSide: "left" | "right"};

// a closer body's standard centre: in from the hinge edge and down from the top of the leaf.
const CLOSER_FROM_HINGE_EDGE = 9 * inches;
const CLOSER_BELOW_TOP = 2 * inches;

export const HARDWARE_PLACEMENT_RULES: HardwarePlacementRule[] = [
    {
        category: "Exit Devices",
        face: "back",
        maxFromStrikeEdge: 5 * inches,
        minAboveFloor: 36 * inches,
    },
    {
        category: "Exit Trims",
        face: "front",
        linkTo: "Exit Devices",
    },
    {
        // parallel-arm mount on the push side: the body centre just under the top of the
        // leaf, in from the hinge edge.
        category: "Closers",
        face: "back",
        standardLocation: leaf => ({
            fromStrikeEdge: leaf.width - CLOSER_FROM_HINGE_EDGE,
            aboveFloor: leaf.height + leaf.undercut - CLOSER_BELOW_TOP,
        }),
    },
];

// the face an item's hardware mounts on, or null when it shows on both.
export function itemFace(item: Pick<HardwareItem, "category" | "category2" | "category3">): "front" | "back" | null {
    return ruleForItem(item)?.face ?? null;
}

// whether `from`'s rule names a category on `to`'s path: `from` follows `to`.
export function linksTo(from: HardwareCategoryFields, to: HardwareCategoryFields): boolean {
    const category = ruleForItem(from)?.linkTo;
    return !!category && hasCategory(to, category);
}

// whether the two items' placements line up by rule, either way round.
export function itemsLink(a: HardwareCategoryFields, b: HardwareCategoryFields): boolean {
    return linksTo(a, b) || linksTo(b, a);
}

export type HardwarePlacementViolation = {
    // the category whose rule failed.
    category: string;
    message: string;
};

// the merged rule for an item: its category path root-first (category3 > category2 >
// category), each level's rule laid over the one above it.
export function ruleForItem(item: Pick<HardwareItem, "category" | "category2" | "category3">, rules: HardwarePlacementRule[] = HARDWARE_PLACEMENT_RULES): HardwarePlacementRule | null {
    const path = [item.category3, item.category2, item.category].filter((c): c is string => !!c);

    let merged: HardwarePlacementRule | null = null;
    for(const level of path) {
        const rule = rules.find(r => r.category.toLowerCase() === level.toLowerCase());
        if(!rule) continue;
        merged = {...(merged ?? {}), ...rule};
    }

    // a linked item defaults to its partner category's standard location.
    if(merged && merged.standardLocation === undefined && merged.linkTo) {
        const partner = ruleForItem({category: merged.linkTo}, rules);
        if(partner?.standardLocation) merged = {...merged, standardLocation: partner.standardLocation};
    }

    return merged;
}

// the leaf a door placement sits on.
function leafFor(app: ElevationApp, placement: MdHardwarePlacement): RuleLeaf {
    const active = placement.side === "active";
    const door = app.doorSpec;
    return {
        width: active ? door.activeDoorWidth.get() : door.inActiveDoorWidth.get(),
        height: door.actualHeight.get(),
        undercut: door.undercut.get(),
        hingeSide: hingeSideOrDefault(app, active),
    };
}

// the standard hub position (rect offsets) for a door placement: the category rule's
// standard location, else that of the placement its rule links it to (an exit trim takes
// its exit device's), else the seeded primary position by kind, else the leaf centre.
export function standardPosition(app: ElevationApp, placement: MdHardwarePlacement, item: HardwareItem): {x: number; y: number} {
    const leaf = leafFor(app, placement);
    const toX = (fromStrike: number) => leaf.hingeSide === "right" ? fromStrike : leaf.width - fromStrike;
    const toY = (aboveFloor: number) => leaf.height + leaf.undercut - aboveFloor;

    const rule = ruleForItem(item);
    if(rule?.standardLocation) {
        const loc = rule.standardLocation(leaf);
        return {x: toX(loc.fromStrikeEdge), y: toY(loc.aboveFloor)};
    }

    if(rule?.linkTo) {
        const leader = app.hardware.ruleLeaderOf(placement);
        const leaderItem = leader ? (app.hardwareGroup?.hardware ?? []).find(h => h.product === leader.productId) : null;
        if(leader && leaderItem) return standardPosition(app, leader, leaderItem);
    }

    if(placement.kind === "handle") {
        return {x: toX(STANDARD_BACKSET), y: toY(CENTER_LOCK_ABOVE_BOTTOM + leaf.undercut)};
    }
    if(placement.kind === "lock") {
        return {x: toX(STANDARD_BACKSET), y: toY(DEADBOLT_ABOVE_BOTTOM + leaf.undercut)};
    }

    return {x: leaf.width / 2, y: leaf.height / 2};
}

// keep an intended door placement position inside its item's rule limits. Returns the
// position unchanged when no rule applies, the rule only flags, or it's a frame placement.
// Applied by HardwareCrosshairThing to drags and dimension edits before they're stored.
export function clampToRule(app: ElevationApp, placement: MdHardwarePlacement, item: HardwareItem, pos: {x: number; y: number}): {x: number; y: number} {
    if(placement.target !== "door") return pos;

    const rule = ruleForItem(item);
    if(!rule || (rule.enforce ?? "clamp") !== "clamp") return pos;

    const leaf = leafFor(app, placement);
    let {x, y} = pos;

    // strike-edge distance -> x, per which edge the strike is on.
    const toX = (fromStrike: number) => leaf.hingeSide === "right" ? fromStrike : leaf.width - fromStrike;
    const fromStrike = leaf.hingeSide === "right" ? x : leaf.width - x;
    if(rule.maxFromStrikeEdge != null && fromStrike > rule.maxFromStrikeEdge) x = toX(rule.maxFromStrikeEdge);
    if(rule.minFromStrikeEdge != null && fromStrike < rule.minFromStrikeEdge) x = toX(rule.minFromStrikeEdge);

    // floor height -> y, measured down from the leaf top.
    const toY = (aboveFloor: number) => leaf.height + leaf.undercut - aboveFloor;
    const aboveFloor = toY(y);
    if(rule.minAboveFloor != null && aboveFloor < rule.minAboveFloor) y = toY(rule.minAboveFloor);
    if(rule.maxAboveFloor != null && aboveFloor > rule.maxAboveFloor) y = toY(rule.maxAboveFloor);

    return {x, y};
}

// check a door placement against its item's merged rule. Empty when it passes or no rule
// applies. `fmt` renders a distance for the messages.
export function doorPlacementViolations(app: ElevationApp, placement: MdHardwarePlacement, item: HardwareItem, fmt: (mm: number) => string): HardwarePlacementViolation[] {
    if(placement.target !== "door") return [];

    const rule = ruleForItem(item);
    if(!rule) return [];

    const leaf = leafFor(app, placement);
    const x = placement.x.get();
    const y = placement.y.get();
    const fromStrike = leaf.hingeSide === "right" ? x : leaf.width - x;
    const aboveFloor = leaf.height - y + leaf.undercut;

    const out: HardwarePlacementViolation[] = [];
    const fail = (message: string) => out.push({category: rule.category, message});

    if(rule.maxFromStrikeEdge != null && fromStrike > rule.maxFromStrikeEdge) {
        fail(`must be within ${fmt(rule.maxFromStrikeEdge)} of the strike edge (now ${fmt(fromStrike)})`);
    }
    if(rule.minFromStrikeEdge != null && fromStrike < rule.minFromStrikeEdge) {
        fail(`must be at least ${fmt(rule.minFromStrikeEdge)} from the strike edge (now ${fmt(fromStrike)})`);
    }
    if(rule.minAboveFloor != null && aboveFloor < rule.minAboveFloor) {
        fail(`must be at least ${fmt(rule.minAboveFloor)} above the floor (now ${fmt(aboveFloor)})`);
    }
    if(rule.maxAboveFloor != null && aboveFloor > rule.maxAboveFloor) {
        fail(`must be within ${fmt(rule.maxAboveFloor)} of the floor (now ${fmt(aboveFloor)})`);
    }

    return out;
}
